import { zodResolver } from '@hookform/resolvers/zod';
import * as WebBrowser from 'expo-web-browser';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Keyboard,
  Platform,
  ScrollView,
  type TextInput,
  View,
} from 'react-native';
import Toast from 'react-native-toast-message';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { z } from 'zod';

import {
  type AuthTokenResponse,
  useCreateCustomer,
} from '@/api/customers/use-create-customer';
import { useCustomer } from '@/api/customers/use-customer';
import { useLoginCustomerApple } from '@/api/customers/use-login-customer-apple';
import { useLoginCustomerGoogle } from '@/api/customers/use-login-customer-google';
import { AppleLogin } from '@/components/apple-login/apple-login';
import ConfirmOtp from '@/components/confirm-otp';
import { GoogleLogin } from '@/components/google-login';
import Navbar from '@/components/login-modal/navbar';
import { signIn, useAuth } from '@/core';
import mixpanelService from '@/core/clients/mixpanel';
import { useCurrentOrganization } from '@/core/organization';
import useSettingsStore from '@/stores/use-settings-store';
import { Button, ControlledInput, showErrorMessage, Text } from '@/ui';
import { Eon } from '@/ui/icons/eon';
import { Loader } from '@/ui/loader';

import { useModalStore } from '../login-modal/store';
import { NavigationButtonTypes } from '../navigation-button';
import FinishSignup from './finish-signup';

type SignupFormProps = {
  email: string;
  password: string;
};

const signupFormSchema = z
  .object({
    email: z.string().email(),
    password: z.string().min(8, 'Minimum 8 characters'),
    confirmPassword: z.string().min(8, 'Minimum 8 characters'),
  })
  .refine((data: any) => data.password === data.confirmPassword, {
    message: 'Passwords must match.',
    path: ['confirmPassword'],
  });

export type SignupFormType = z.infer<typeof signupFormSchema>;

interface SignupProps {
  onBack: () => void;
}

export default function Signup({ onBack }: SignupProps) {
  const { styles, theme } = useStyles(stylesheet);
  const [showFinishSignup, setShowFinishSignup] = useState(false);
  const [showConfirmOtp, setShowConfirmOtp] = useState(false);
  const [phone, setPhone] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [keyboardPadding, setKeyboardPadding] = useState(0);

  const scrollViewRef = useRef<ScrollView>(null);
  const passwordViewRef = useRef<View>(null);
  const confirmPasswordViewRef = useRef<View>(null);
  const passwordInputRef = useRef<TextInput>(null);
  const confirmPasswordInputRef = useRef<TextInput>(null);
  const keyboardHeightRef = useRef(0);
  const scrollOffsetRef = useRef(0);
  const setLoginModalMode = useModalStore((state) => state.setLoginModalMode);
  const setFinishingSignup = useModalStore((state) => state.setFinishingSignup);
  const refCode = useSettingsStore((state) => state.refCode);
  const promoCode = useSettingsStore((state) => state.promoCode);
  const { slug: organizationSlug } = useCurrentOrganization();

  const status = useAuth.use.status();
  const { data: customer, refetch: refetchCustomer } = useCustomer();

  useEffect(() => {
    mixpanelService.track('Page Viewed', {
      'Page Name': 'Sign Up',
    });
  }, []);

  // Android only: Modal renders as a Dialog whose windowSoftInputMode can't be set
  // from JS, so the window never resizes when the keyboard appears. We manually track
  // keyboard height to add scroll room (paddingBottom) and compute scroll targets.
  // keyboardPadding is never reset on hide — keeps the spacer stable during field transitions.
  useEffect(() => {
    if (Platform.OS !== 'android') return;

    const showSub = Keyboard.addListener('keyboardDidShow', (e) => {
      keyboardHeightRef.current = e.endCoordinates.height;
      setKeyboardPadding(e.endCoordinates.height);
    });
    const hideSub = Keyboard.addListener('keyboardDidHide', () => {
      keyboardHeightRef.current = 0;
    });
    return () => {
      showSub.remove();
      hideSub.remove();
    };
  }, []);

  // Scrolls a field's wrapper View above the keyboard. Called from onSubmitEditing
  // (Next key) so the keyboard is already up and keyboardHeightRef is populated.
  const scrollToView = useCallback((view: View) => {
    // getNativeScrollRef() gives the host node that exposes the measure family;
    // the ScrollView instance itself doesn't.
    const node = scrollViewRef.current?.getNativeScrollRef() as
      | View
      | undefined;
    if (!node) return;
    const kbH = keyboardHeightRef.current;
    if (kbH === 0) return;
    node.measureInWindow((...svArgs: [number, number, number, number]) => {
      const [, , , svH] = svArgs;
      // measureLayout gives scroll-content-relative coordinates, avoiding the
      // window-vs-screen offset (~28px) from the edge-to-edge Dialog window.
      view.measureLayout(
        node,
        (...fieldArgs: [number, number, number, number]) => {
          const [, fieldY, , fieldH] = fieldArgs;
          const visibleArea = svH - kbH;
          const fieldBottom = fieldY + fieldH + 16; // 16px clearance above keyboard
          if (fieldBottom > scrollOffsetRef.current + visibleArea) {
            scrollViewRef.current?.scrollTo({
              y: fieldBottom - visibleArea,
              animated: true,
            });
          }
        },
        () => {}
      );
    });
  }, []);

  useEffect(() => {
    if (status === 'signIn' && customer && !customer.setupComplete) {
      setFinishingSignup(true);
      setShowFinishSignup(true);
    }
  }, [status, customer, setFinishingSignup]);

  const { mutate: createCustomer, isPending: isCreatingCustomer } =
    useCreateCustomer();

  const { mutate: loginCustomerGoogle, isPending: isLoggingInCustomerGoogle } =
    useLoginCustomerGoogle();

  const { mutate: loginCustomerApple, isPending: isLoggingInCustomerApple } =
    useLoginCustomerApple();

  const {
    handleSubmit,
    control,
    trigger,
    setValue,
    formState: { isValid },
  } = useForm<SignupFormType>({
    resolver: zodResolver(signupFormSchema),
  });

  const handleCreateCustomer = (data: SignupFormProps) => {
    mixpanelService.track('Button Clicked', {
      'Button Name': 'Sign Up Email',
    });

    createCustomer(
      { ...data, referralCode: refCode, promoCode: promoCode },
      {
        onSuccess: (respData: AuthTokenResponse) => {
          mixpanelService.track('Sign Up Successful');

          setFinishingSignup(true);
          setShowFinishSignup(true);

          signIn(respData.data.token);
        },
        onError: (e) => {
          mixpanelService.track('Sign Up Failed');
          setErrorMsg(e.message);
          showErrorMessage(e.message);
        },
      }
    );
  };

  const handleGoogleLogin = (credential: string) => {
    loginCustomerGoogle(
      {
        googleCredential: credential,
        referralCode: refCode,
        promoCode: promoCode,
      },
      {
        onSuccess: (respData: AuthTokenResponse) => {
          mixpanelService.track('Sign Up Successful');

          Toast.show({
            text1: 'Signed up successfully',
            type: 'success',
          });

          if (!respData.data.setupComplete) {
            setFinishingSignup(true);
            setShowFinishSignup(true);
          }

          signIn(respData.data.token);
        },
        onError: (e) => {
          mixpanelService.track('Sign Up Failed');
          showErrorMessage(e.message);
        },
      }
    );
  };

  const handleAppleLogin = (
    userId: string,
    identityToken: string,
    fullName: string
  ) => {
    loginCustomerApple(
      {
        userId,
        identityToken,
        fullName,
        referralCode: refCode,
        promoCode: promoCode,
      },
      {
        onSuccess: (respData: AuthTokenResponse) => {
          mixpanelService.track('Sign Up Successful');

          Toast.show({
            text1: 'Signed up successfully',
            type: 'success',
          });

          if (!respData.data.setupComplete) {
            setShowFinishSignup(true);
            setFinishingSignup(true);
          }

          signIn(respData.data.token);
        },
        onError: (e) => {
          mixpanelService.track('Sign Up Failed');
          showErrorMessage(e.message);
        },
      }
    );
  };

  const handleBlur = (fieldName: string) => {
    trigger(fieldName);
  };

  const onFinishSignupDone = (phone: string) => {
    setShowFinishSignup(false);
    setShowConfirmOtp(true);
    setPhone(phone);
  };

  const onConfirmOtpDone = () => {
    setShowConfirmOtp(false);
    setFinishingSignup(false);
    refetchCustomer();
  };

  const onBackPress = () => {
    if (showConfirmOtp) {
      setShowConfirmOtp(false);
      setShowFinishSignup(true);
      return;
    }

    if (showFinishSignup) {
      setShowFinishSignup(false);
      return;
    }

    onBack();
  };

  if (isCreatingCustomer || isLoggingInCustomerGoogle) {
    return <Loader />;
  }

  const renderBody = () => {
    if (showFinishSignup) {
      return (
        <FinishSignup
          onBack={() => {
            setShowFinishSignup(false);
          }}
          onDone={onFinishSignupDone}
        />
      );
    }

    if (showConfirmOtp) {
      return (
        <ConfirmOtp
          phone={phone}
          onBack={() => {
            setShowConfirmOtp(false);
            setShowFinishSignup(true);
          }}
          onDone={onConfirmOtpDone}
        />
      );
    }

    return (
      <ScrollView
        ref={scrollViewRef}
        style={{ flexGrow: 1 }}
        // paddingBottom = keyboard height gives scrollTo room to move.
        // Without it the content fits the frame and scrollTo is a no-op.
        contentContainerStyle={{ flexGrow: 1, paddingBottom: keyboardPadding }}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        scrollEventThrottle={16}
        onScroll={(e) => {
          scrollOffsetRef.current = e.nativeEvent.contentOffset.y;
        }}
      >
        <Navbar
          onPress={onBackPress}
          type={
            Platform.OS === 'web'
              ? NavigationButtonTypes.cancel
              : NavigationButtonTypes.back_with_text
          }
        />

        <View style={styles.container}>
          <View style={styles.bottomContainer}>
            <View style={styles.topContainer}>
              <View style={styles.titleContainer}>
                {Platform.OS !== 'web' && <Eon width="48" height="18" />}

                <Text tx="signup.subtitle" type="title1" />
              </View>

              <View style={styles.inputsContainer}>
                <ControlledInput
                  control={control}
                  name="email"
                  label="Email"
                  spellCheck={false}
                  onBlur={() => {
                    handleBlur('email');
                  }}
                  onChangeText={(text) => {
                    trigger('email');
                    setValue('email', text);
                  }}
                  autoComplete="email"
                  returnKeyType="next"
                  blurOnSubmit={false}
                  onSubmitEditing={() => {
                    passwordInputRef.current?.focus();
                    if (passwordViewRef.current)
                      scrollToView(passwordViewRef.current);
                  }}
                />

                {/* collapsable={false}: Android Paper flattens layout-only Views out of the
                    native hierarchy, making them unmeasurable via measureLayout. */}
                <View ref={passwordViewRef} collapsable={false}>
                  <ControlledInput
                    control={control}
                    name="password"
                    label="Password"
                    secureTextEntry
                    nativeInputRef={passwordInputRef}
                    returnKeyType="next"
                    blurOnSubmit={false}
                    onSubmitEditing={() => {
                      confirmPasswordInputRef.current?.focus();
                      if (confirmPasswordViewRef.current)
                        scrollToView(confirmPasswordViewRef.current);
                    }}
                    onBlur={() => {
                      handleBlur('password');
                    }}
                    onChangeText={(text) => {
                      setValue('password', text);
                      trigger('password');
                    }}
                  />
                </View>

                <View ref={confirmPasswordViewRef} collapsable={false}>
                  <ControlledInput
                    control={control}
                    name="confirmPassword"
                    label="Confirm password"
                    secureTextEntry
                    nativeInputRef={confirmPasswordInputRef}
                    returnKeyType="done"
                    onSubmitEditing={() => handleSubmit(handleCreateCustomer)()}
                    onBlur={() => {
                      handleBlur('confirmPassword');
                    }}
                    onChangeText={(text) => {
                      setValue('confirmPassword', text);
                      trigger('confirmPassword');
                    }}
                  />
                </View>
              </View>

              <Button
                tx="signin.continue"
                type="primary"
                onPress={handleSubmit(handleCreateCustomer)}
                disabled={!isValid}
                style={{ width: '100%' }}
              />

              {errorMsg ? (
                <View>
                  <Text
                    type="bodyRegular"
                    style={{ color: theme.colors.error }}
                  >
                    {errorMsg}
                  </Text>
                </View>
              ) : null}
            </View>

            {!organizationSlug ? (
              <>
                <View style={{ alignItems: 'center' }}>
                  <Text tx="signin.or" type="bodyRegular" style={styles.body} />
                </View>

                <View style={{ gap: 16, alignItems: 'center' }}>
                  <GoogleLogin
                    onSuccess={handleGoogleLogin}
                    tx="signup.googleButton"
                    onPress={() => {
                      mixpanelService.track('Button Clicked', {
                        'Button Name': 'Sign Up Google',
                      });
                    }}
                  />
                  <AppleLogin
                    tx="signup.appleButton"
                    onSuccess={handleAppleLogin}
                    onPress={() => {
                      mixpanelService.track('Button Clicked', {
                        'Button Name': 'Sign Up Apple',
                      });
                    }}
                  />
                </View>
              </>
            ) : null}

            <Button
              tx="signup.alreadyHaveAcct"
              type="ghost"
              onPress={() => {
                setLoginModalMode('signin');
              }}
            />

            <View
              style={{
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: 4,
                justifyContent: 'center',
              }}
            >
              <Text type="caption1">
                By clicking continue, you agree to our
              </Text>
              <Text
                type="caption1"
                style={{
                  textDecorationLine: 'underline',
                }}
                onPress={async () => {
                  if (Platform.OS === 'web') {
                    window.open(
                      'https://www.eonrides.com/legal/rental-terms',
                      '_blank'
                    );
                  } else {
                    await WebBrowser.openBrowserAsync(
                      'https://www.eonrides.com/legal/rental-terms'
                    );
                  }
                }}
              >
                Terms of Service
              </Text>
              <Text type="caption1"> and</Text>
              <Text
                type="caption1"
                style={{
                  textDecorationLine: 'underline',
                }}
                onPress={async () => {
                  if (Platform.OS === 'web') {
                    window.open(
                      'https://www.eonrides.com/legal/terms-privacy-policy',
                      '_blank'
                    );
                  } else {
                    await WebBrowser.openBrowserAsync(
                      'https://www.eonrides.com/legal/terms-privacy-policy'
                    );
                  }
                }}
              >
                Privacy Policy
              </Text>
            </View>
          </View>
        </View>
      </ScrollView>
    );
  };

  return (
    <View
      style={{
        width: '100%',
        height: Platform.OS !== 'web' ? '100%' : undefined,
        backgroundColor: theme.colors.background_2,
        borderRadius: 16,
      }}
    >
      {renderBody()}
    </View>
  );
}

const stylesheet = createStyleSheet(() => ({
  container: {
    flexGrow: 1,
    paddingLeft: 24,
    paddingRight: 24,
    paddingBottom: 24,
    gap: 16,
  },
  bottomContainer: {
    flexGrow: 1,
    gap: 16,
  },
  topContainer: {
    gap: 16,
  },
  titleContainer: {
    gap: 16,
  },
  inputsContainer: {
    gap: 16,
  },
  body: {
    color: 'rgba(234, 237, 247, 0.50)',
  },
}));
